import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { AsyncSelect } from "@/components/AsyncSelect";
import { companyAtom } from "@/store/company";
import {
  Facility,
  ElectricityFacility,
  GarantiaOrigen,
  Comercializadora,
} from "@/types";
import { PlusCircle, Trash2, Upload } from "lucide-react";
import { toast } from "react-toastify";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { prepareDisplayDate } from "@/lib/utils";

interface ElectricityFormProps {
  electricityId: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const formSchema = z.object({
  quantity: z
    .number()
    .min(0.01, { message: "La cantidad debe ser mayor que 0" }),
  invoice_amount: z.number().min(0),
  invoice: z.string().nullable(),
  invoice_date: z.string().nullable(),
  start_date: z.string(),
  end_date: z.string().nullable(),
  comments: z.string().nullable(),
  gdo: z.number(),
  comercializadora: z.number(),
  document: z.instanceof(File).optional().nullable(),
  facilities: z
    .array(
      z.object({
        facility: z.number(),
        assigned: z.number().min(0).max(100),
      }),
    )
    .refine(
      (facilities) => {
        const facilityIds = facilities.map((f) => f.facility);
        return new Set(facilityIds).size === facilityIds.length;
      },
      {
        message: "Each facility can only be assigned once",
        path: ["facilities"],
      },
    )
    .refine(
      (facilities) => {
        const sum = facilities.reduce((acc, f) => acc + f.assigned, 0);
        return sum === 100;
      },
      {
        message: "The sum of all percentages must be 100%",
        path: ["facilities"],
      },
    ),
});

type FormValues = z.infer<typeof formSchema>;

export function ElectricityForm({
  electricityId,
  onSuccess,
  onCancel,
}: ElectricityFormProps) {
  const { t } = useTranslation();
  const [company] = useAtom(companyAtom);
  const queryClient = useQueryClient();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [currentFileType, setCurrentFileType] = useState<string>("");
  const [isImagePreviewError, setIsImagePreviewError] = useState(false);
  const { token } = useAuth();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      quantity: 0,
      invoice_amount: 0,
      invoice: "",
      invoice_date: null,
      comments: null,
      gdo: undefined,
      comercializadora: undefined,
      document: null,
      facilities: [
        {
          facility: undefined,
          assigned: 100,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "facilities",
  });

  // Query for GDO (Garantia de Origen)
  const gdoQuery = useQuery<GarantiaOrigen[]>({
    // GDO Query - Start
    queryKey: ["garantia-origen", token],
    queryFn: async () => {
      if (!token)
        throw new Error("Authentication token not available for GDO fetch");
      const response = await fetch(getApiUrl(`garantia-origen/?limit=9999`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch GDOs:", errorText);
        throw new Error(
          `Failed to fetch GDOs: ${response.statusText} - ${errorText.substring(0, 100)}`,
        );
      }
      const data = await response.json();
      return data.results || data || []; // Handle wrapped, direct array, or empty
    },
    enabled: !!token, // GDO Query - End
  });

  // Fetcher function for comercializadoras (used by AsyncSelect)
  const fetchComercializadoras = async (
    query?: string,
  ): Promise<Comercializadora[]> => {
    try {
      // If query is a number, it's likely an ID lookup for initialization
      const isIdLookup = query && /^\d+$/.test(query);

      if (isIdLookup) {
        // First, check if we already have this comercializadora in our electricity data
        if (
          electricity?.comercializadora &&
          electricity.comercializadora.id.toString() === query
        ) {
          return [electricity.comercializadora];
        }

        // Fallback to API call
        const response = await fetch(
          getApiUrl(`/comercializadoras/${query}/`),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (!response.ok) {
          throw new Error("Failed to fetch company");
        }
        const res = await response.json();
        return [res];
      } else {
        // Search by name
        const searchParam = query ? `&name=${encodeURIComponent(query)}` : "";
        const response = await fetch(
          getApiUrl(`/comercializadoras/?limit=20${searchParam}`),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (!response.ok) {
          throw new Error("Failed to fetch company");
        }
        const res = await response.json();
        return res.results || [];
      }
    } catch (error) {
      console.error("Error fetching comercializadoras:", error);
      return [];
    }
  };

  // Query for facilities
  const facilitiesQuery = useQuery<Facility[]>({
    queryKey: ["facilities", company?.id],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`/facilities/?limit=9999&company=${company?.id}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch facilities");
      }
      const res = await response.json();
      return res.results;
    },
    enabled: !!company?.id,
  });

  // Query for electricity data if editing
  const electricityQuery = useQuery({
    queryKey: ["electricity", electricityId],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`/electricities/${electricityId}/`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch company");
      }
      return response.json();
    },
    enabled: !!electricityId,
  });

  const isLoading =
    gdoQuery.isLoading ||
    facilitiesQuery.isLoading ||
    (electricityId && electricityQuery.isLoading) ||
    (electricityId && !electricityQuery.data);

  const isError =
    gdoQuery.isError ||
    facilitiesQuery.isError ||
    (electricityId && electricityQuery.isError);

  const gdoOptions = gdoQuery.data;
  const facilities = facilitiesQuery.data;
  const electricity = electricityQuery.data;

  useEffect(() => {
    if (electricityId) {
      if (electricity && gdoOptions && facilities) {
        let facilitiesData;

        if (electricity.facilities && electricity.facilities.length > 0) {
          facilitiesData = electricity.facilities.map(
            (facility: ElectricityFacility) => ({
              facility: facility.facility.id,
              assigned: facility.assigned,
            }),
          );
        } else if (electricity.facility && electricity.facility.id) {
          facilitiesData = [
            {
              facility: electricity.facility.id,
              assigned: electricity.assigned || 100,
            },
          ];
        } else {
          facilitiesData = [
            {
              facility: undefined,
              assigned: 100,
            },
          ];
        }

        const gdoId = electricity.gdo?.id || electricity.gdo;
        const comercializadoraId =
          electricity.comercializadora?.id || electricity.comercializadora;

        const formData = {
          quantity: +electricity.quantity,
          invoice_amount: +electricity.invoice_amount,
          invoice: electricity.invoice,
          invoice_date: electricity.invoice_date,
          start_date: electricity.start_date,
          end_date: electricity.end_date,
          comments: electricity.comments,
          gdo: gdoId,
          comercializadora: comercializadoraId,
          document: null,
          facilities: facilitiesData,
        };

        form.reset(formData);

        if (electricity.document_url) {
          // URL already comes with authentication parameters from backend
          console.log("Document URL from backend:", electricity.document_url);
          setDocumentUrl(electricity.document_url);
          // Detect file type from URL extension for proper preview
          const url = electricity.document_url.toLowerCase();
          if (
            url.endsWith(".jpg") ||
            url.endsWith(".jpeg") ||
            url.includes(".jpg?") ||
            url.includes(".jpeg?") ||
            url.endsWith(".png") ||
            url.includes(".png?") ||
            url.endsWith(".webp") ||
            url.includes(".webp?") ||
            url.includes("jpg") ||
            url.includes("jpeg") ||
            url.includes("png")
          ) {
            console.log("Setting image type for preview");
            setCurrentFileType("image/jpeg"); // Default to image type for preview
          } else if (
            url.endsWith(".pdf") ||
            url.includes(".pdf?") ||
            url.includes("pdf")
          ) {
            console.log("Setting PDF type for preview");
            setCurrentFileType("application/pdf");
          } else {
            // Try to detect from content-type or default to image for preview
            console.log(
              "Could not detect file type from URL, defaulting to image",
            );
            setCurrentFileType("image/jpeg");
          }
        }

        setIsImagePreviewError(false);
      }
    } else {
      form.reset({
        quantity: 0,
        invoice_amount: 0,
        invoice: "",
        invoice_date: null,
        comments: null,
        gdo: undefined,
        comercializadora: undefined,
        document: null,
        facilities: [
          {
            facility: undefined,
            assigned: 100,
          },
        ],
      });
    }
  }, [electricityId, electricity, gdoOptions, facilities, form]);

  const createMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "facilities" && Array.isArray(value)) {
          value.forEach(
            (
              facility: { facility: number; assigned: number },
              index: number,
            ) => {
              formData.append(
                `facilities[${index}]`,
                JSON.stringify({
                  facility: facility.facility,
                  assigned: facility.assigned,
                }),
              );
            },
          );
        } else if (key === "document" && value instanceof File) {
          formData.append("document", value);
        } else if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      const response = await fetch(getApiUrl("/electricities/"), {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to create electricity");
      }
      const res = await response.json();
      return res;
    },
    onSuccess: () => {
      toast.success(t("common.success.created"));
      queryClient.invalidateQueries({ queryKey: ["electricity-facilities"] });
      onSuccess();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const formData = new FormData();

      const { facilities, document, ...restData } = data;

      Object.entries(restData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      if (document instanceof File) {
        formData.append("document", document);
      }

      if (facilities && facilities.length > 0) {
        const facilitiesJson = JSON.stringify(facilities);
        formData.append("facilities", facilitiesJson);
      }

      const response = await fetch(
        getApiUrl(`/electricities/${electricityId}/`),
        {
          method: "PATCH",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail:
            "Failed to update electricity and could not parse error response.",
        }));
        throw new Error(errorData.detail || "Failed to update electricity");
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success(t("common.success.updated"));
      queryClient.invalidateQueries({ queryKey: ["electricity-facilities"] });
      onSuccess();
    },
  });

  const onSubmit = (values: FormValues) => {
    const totalPercentage = values.facilities.reduce(
      (sum, facility) => sum + facility.assigned,
      0,
    );
    if (totalPercentage !== 100) {
      toast.error(t("electricity.error.percentage_sum"));
      return;
    }

    const facilityIds = values.facilities.map((f) => f.facility);
    const uniqueFacilityIds = new Set(facilityIds);
    if (uniqueFacilityIds.size !== facilityIds.length) {
      toast.error(t("electricity.error.duplicate_facility"));
      return;
    }

    if (electricityId) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const addFacility = () => {
    const currentTotal = form
      .getValues()
      .facilities.reduce((sum, facility) => sum + (facility.assigned || 0), 0);

    const remainingPercentage = Math.max(0, 100 - currentTotal);

    append({
      facility: undefined,
      assigned: remainingPercentage,
    });
  };

  const handleRemoveFacility = (index: number) => {
    if (fields.length > 1) {
      const removedPercentage =
        form.getValues().facilities[index].assigned || 0;
      remove(index);

      if (removedPercentage > 0 && fields.length > 0) {
        const facilities = form.getValues().facilities;
        const firstFacilityIndex = 0;
        const newPercentage =
          (facilities[firstFacilityIndex].assigned || 0) + removedPercentage;

        form.setValue(
          `facilities.${firstFacilityIndex}.assigned`,
          newPercentage,
        );
      }
    } else {
      toast.error(t("electricity.error.min_one_facility"));
    }
  };

  const isFacilitySelected = (facilityId: number, currentIndex: number) => {
    return form
      .getValues()
      .facilities.some(
        (f, idx) => idx !== currentIndex && f.facility === facilityId,
      );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      let detectedType = file.type || "";
      if (!detectedType && file.name) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "jpg" || ext === "jpeg") detectedType = "image/jpeg";
        else if (ext === "png") detectedType = "image/png";
        else if (ext === "webp") detectedType = "image/webp";
        else if (ext === "pdf") detectedType = "application/pdf";
      }

      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/jpg",
        "image/*",
      ];

      const isAllowed = allowedTypes.some((type) => {
        if (type === "image/*") {
          return detectedType.startsWith("image/");
        }
        return detectedType === type;
      });

      if (!isAllowed) {
        toast.error(t("electricity.error.pdf_or_image_only"));
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(t("electricity.error.file_too_large"));
        return;
      }

      if (documentUrl && documentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentUrl);
      }

      const newUrl = URL.createObjectURL(file);
      setDocumentUrl(newUrl);
      setCurrentFileType(detectedType);
      setIsImagePreviewError(false);
      form.setValue("document", file, { shouldValidate: true });
    } catch (error) {
      console.error("Error handling file change:", error);
      toast.error(t("common.error.unknown"));
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="h-full flex flex-col"
      >
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto">
          {/* Document Preview/Upload Section */}
          <div className="md:w-1/2 p-4 border-r">
            <div className="flex flex-col h-full">
              <h3 className="text-lg font-medium mb-4">
                {t("electricity.fields.document")}
              </h3>

              {electricityId && electricityQuery.isLoading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-muted-foreground">{t("common.loading")}</p>
                </div>
              ) : documentUrl ? (
                <div className="flex flex-col h-full">
                  <div className="flex flex-1 items-center justify-center min-h-[400px] border rounded-md overflow-hidden bg-white">
                    {/* Preview PDF or image */}
                    {(() => {
                      console.log(
                        "Rendering preview with type:",
                        currentFileType,
                        "URL:",
                        documentUrl,
                      );
                      if (currentFileType.startsWith("image/") && documentUrl) {
                        return (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-full h-full flex items-center justify-center">
                              {isImagePreviewError ? (
                                <div className="p-4 text-center">
                                  <p>{t("common.nopreviewload")}</p>
                                  <a
                                    href={documentUrl || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary underline"
                                  >
                                    {t("common.opennewtab")}
                                  </a>
                                </div>
                              ) : (
                                <img
                                  src={documentUrl || ""}
                                  alt={t("combustion.fields.document")}
                                  className="object-contain max-w-full max-h-full"
                                />
                              )}
                            </div>
                          </div>
                        );
                      } else if (documentUrl) {
                        // Default to PDF preview
                        return (
                          <object
                            data={documentUrl}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                            className="min-h-[400px]"
                          >
                            <div className="p-4">
                              <p>{t("electricity.pdf_not_supported")}</p>
                              <a
                                href={documentUrl || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                {t("electricity.download_pdf")}
                              </a>
                            </div>
                          </object>
                        );
                      }
                    })()}
                  </div>

                  <div className="mt-4">
                    <label htmlFor="document-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center p-4 border-2 border-dashed rounded-md hover:bg-muted transition-colors">
                        <Upload className="h-6 w-6 mr-2" />
                        <span>{t("electricity.replace_document")}</span>
                      </div>
                      <input
                        id="document-upload"
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center h-full">
                  <label
                    htmlFor="document-upload"
                    className="cursor-pointer w-full"
                  >
                    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-md hover:bg-muted transition-colors min-h-[400px]">
                      <Upload className="h-12 w-12 mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium">
                        {t("electricity.upload_document_any")}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t("electricity.pdf_or_image_only")}
                      </p>
                    </div>
                    <input
                      id="document-upload"
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields Section */}
          <div className="md:w-1/2 flex-1 flex flex-col space-y-4 overflow-y-auto p-4">
            <FormField
              control={form.control}
              name="comercializadora"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <AsyncSelect<Comercializadora>
                        value={field.value ? String(field.value) : ""}
                        onChange={(value) => field.onChange(parseInt(value))}
                        fetcher={fetchComercializadoras}
                        label={t("electricity.fields.comercializadora")}
                        placeholder={t("electricity.fields.comercializadora")}
                        getOptionValue={(option) => option.id.toString()}
                        getDisplayValue={(option) => option.name}
                        renderOption={(option) => (
                          <div className="flex items-center">
                            <span>{option.name}</span>
                          </div>
                        )}
                        notFound={
                          <div className="py-6 text-center text-sm">
                            {t("common.no_results_found")}
                          </div>
                        }
                        width="100%"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="gdo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("electricity.fields.gdo")}</FormLabel>
                  <Select
                    value={field.value ? field.value.toString() : undefined}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("electricity.select_gdo")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoading ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          {t("common.loading")}...
                        </div>
                      ) : isError ? (
                        <div className="p-2 text-sm text-destructive">
                          {t("common.error_loading_data")}
                        </div>
                      ) : Array.isArray(gdoOptions) ? (
                        gdoOptions.map((gdo) => (
                          <SelectItem key={gdo.id} value={gdo.id.toString()}>
                            {gdo.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground">
                          {t("common.no_data_available")}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("electricity.fields.quantity")}</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl className="flex-1">
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value))
                        }
                      />
                    </FormControl>
                    <div className="text-muted-foreground">kWh</div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="invoice"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("electricity.fields.invoice")}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="invoice_amount"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t("electricity.fields.invoice_amount")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("electricity.fields.start_date")}</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={
                          field.value
                            ? prepareDisplayDate(field.value)
                            : undefined
                        }
                        setDate={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = date.getMonth();
                            const day = date.getDate();
                            const correctedDate = new Date(
                              Date.UTC(year, month, day),
                            );
                            field.onChange(
                              correctedDate.toISOString().split("T")[0],
                            );
                          } else {
                            field.onChange(null);
                          }
                        }}
                        disableFutureDates={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("electricity.fields.end_date")}</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={
                          field.value
                            ? prepareDisplayDate(field.value)
                            : undefined
                        }
                        setDate={(date: Date | null) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = date.getMonth();
                            const day = date.getDate();
                            const correctedDate = new Date(
                              Date.UTC(year, month, day),
                            );
                            field.onChange(
                              correctedDate.toISOString().split("T")[0],
                            );
                          } else {
                            field.onChange(null);
                          }
                        }}
                        disableFutureDates={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="invoice_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("electricity.fields.invoice_date")}</FormLabel>
                  <FormControl>
                    <DatePicker
                      date={
                        field.value
                          ? prepareDisplayDate(field.value)
                          : undefined
                      }
                      setDate={(date: Date | null) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = date.getMonth();
                          const day = date.getDate();
                          const correctedDate = new Date(
                            Date.UTC(year, month, day),
                          );
                          field.onChange(
                            correctedDate.toISOString().split("T")[0],
                          );
                        } else {
                          field.onChange(null);
                        }
                      }}
                      disableFutureDates={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Facilities Assignment Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium">
                  {t("electricity.fields.facilities")}
                </h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFacility}
                  className="flex items-center gap-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  {t("electricity.add_facility")}
                </Button>
              </div>

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-end gap-4 p-3 border rounded-md"
                >
                  <div className="flex-grow">
                    <FormField
                      control={form.control}
                      name={`facilities.${index}.facility`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel>
                            {t("electricity.fields.facility")}
                          </FormLabel>
                          <Select
                            value={
                              field.value ? field.value.toString() : undefined
                            }
                            onValueChange={(value) =>
                              field.onChange(parseInt(value))
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t("electricity.select_facility")}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Array.isArray(facilities) ? (
                                facilities
                                  .filter(
                                    (facility) =>
                                      !isFacilitySelected(facility.id, index),
                                  )
                                  .map((facility) => (
                                    <SelectItem
                                      key={facility.id}
                                      value={facility.id.toString()}
                                    >
                                      {facility.name}
                                    </SelectItem>
                                  ))
                              ) : (
                                <div className="p-2 text-sm text-muted-foreground">
                                  {t("common.no_data_available")}
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name={`facilities.${index}.assigned`}
                    render={({ field }) => (
                      <FormItem className="w-32">
                        <FormLabel>
                          {t("electricity.fields.assigned")}
                        </FormLabel>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value))
                              }
                            />
                          </FormControl>
                          <span className="text-sm text-muted-foreground">
                            %
                          </span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveFacility(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("electricity.fields.comments")}</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Fixed Footer Buttons */}
        <div className="flex justify-end gap-2 p-4 border-t bg-background">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? t("common.saving")
              : electricityId
                ? t("common.save")
                : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
